import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  VpnKey as KeyIcon,
  AutoFixHigh as GenerateIcon,
  Block as RevokeIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import csApiService, { CsApiConfig } from '../services/csApiService';
import spopService from '../services/spopService';

const CsApiSettings: React.FC = () => {
  const [config, setConfig] = useState<CsApiConfig | null>(null);
  const [allowedSpops, setAllowedSpops] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newKeyDialog, setNewKeyDialog] = useState(false);
  const [generatedKey, setGeneratedKey] = useState('');
  const [confirmGenerate, setConfirmGenerate] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const { data: spops = [] } = useQuery({
    queryKey: ['spops-all-cs-api'],
    queryFn: async () => {
      const res = await spopService.getAllSPOPs();
      return res.data?.data?.spops || [];
    },
  });

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await csApiService.getConfig();
      if (res.success && res.data) {
        setConfig(res.data);
        setAllowedSpops(res.data.allowed_spops || []);
      } else {
        setError(res.message || 'Gagal memuat konfigurasi');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Gagal memuat konfigurasi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess(`${label} berhasil disalin`);
    } catch {
      setError('Gagal menyalin ke clipboard');
    }
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setError('');
      setSuccess('');
      const res = await csApiService.generateKey();
      if (res.success && res.data?.api_key) {
        setGeneratedKey(res.data.api_key);
        setConfig(res.data);
        setAllowedSpops(res.data.allowed_spops || []);
        setConfirmGenerate(false);
        setNewKeyDialog(true);
        setSuccess(res.message || 'API key berhasil dibuat');
      } else {
        setError(res.message || 'Gagal membuat API key');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Gagal membuat API key');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const res = await csApiService.updateConfig({
        is_enabled: config.is_enabled,
        allowed_spops: allowedSpops,
      });
      if (res.success) {
        setConfig(res.data);
        setAllowedSpops(res.data.allowed_spops || []);
        setSuccess('Konfigurasi berhasil disimpan');
      } else {
        setError(res.message || 'Gagal menyimpan');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async () => {
    try {
      setRevoking(true);
      setError('');
      setSuccess('');
      const res = await csApiService.revokeKey();
      if (res.success) {
        setConfig(res.data);
        setAllowedSpops([]);
        setConfirmRevoke(false);
        setSuccess(res.message || 'API key dicabut');
      } else {
        setError(res.message || 'Gagal mencabut API key');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Gagal mencabut API key');
    } finally {
      setRevoking(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="CS AI Remote API"
          subheader="API key untuk aplikasi Customer Service AI mengakses data pelanggan & tagihan (read-only)"
          action={
            <Chip
              label={config?.is_enabled && config?.has_api_key ? 'Aktif' : 'Nonaktif'}
              color={config?.is_enabled && config?.has_api_key ? 'success' : 'default'}
            />
          }
        />
        <CardContent>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Base URL API
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={config?.api_base_url || ''}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Salin URL">
                        <IconButton
                          edge="end"
                          onClick={() => copyText(config?.api_base_url || '', 'Base URL')}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                API Key saat ini
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={config?.has_api_key ? config.api_key_masked : 'Belum ada API key'}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <KeyIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                helperText={
                  config?.last_generated_at
                    ? `Terakhir dibuat: ${new Date(config.last_generated_at).toLocaleString('id-ID')}`
                    : 'Generate API key untuk integrasi CS AI'
                }
              />
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                startIcon={<GenerateIcon />}
                onClick={() => setConfirmGenerate(true)}
                disabled={generating}
              >
                {config?.has_api_key ? 'Generate Key Baru' : 'Generate API Key'}
              </Button>
              {config?.has_api_key && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<RevokeIcon />}
                  onClick={() => setConfirmRevoke(true)}
                  disabled={revoking}
                >
                  Cabut API Key
                </Button>
              )}
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadConfig}>
                Muat Ulang
              </Button>
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={!!config?.is_enabled}
                  onChange={(e) =>
                    setConfig((prev) => (prev ? { ...prev, is_enabled: e.target.checked } : prev))
                  }
                  disabled={!config?.has_api_key}
                />
              }
              label="Aktifkan akses CS AI API"
            />

            <FormControl fullWidth size="small">
              <InputLabel>Batasi SPOP (opsional)</InputLabel>
              <Select
                multiple
                label="Batasi SPOP (opsional)"
                value={allowedSpops}
                onChange={(e) => setAllowedSpops(e.target.value as string[])}
                renderValue={(selected) =>
                  (selected as string[])
                    .map((id) => spops.find((s: any) => s.id === id)?.name || id)
                    .join(', ')
                }
              >
                {spops.map((spop: any) => (
                  <MenuItem key={spop.id} value={spop.id}>
                    {spop.name} ({spop.areaCode})
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Kosongkan untuk mengizinkan semua SPOP
              </Typography>
            </FormControl>

            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Cara pakai di aplikasi CS AI" />
        <CardContent>
          <Typography variant="body2" paragraph>
            Kirim header berikut di setiap request:
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={`X-CS-API-Key: ${config?.api_key_masked || '<api-key-anda>'}`}
            InputProps={{ readOnly: true }}
            sx={{ mb: 2, fontFamily: 'monospace' }}
          />
          <Typography variant="body2" color="text.secondary">
            Dokumentasi lengkap endpoint: <code>docs/CS_AI_REMOTE_API.md</code> di repository proyek.
          </Typography>
        </CardContent>
      </Card>

      <Dialog open={confirmGenerate} onClose={() => setConfirmGenerate(false)}>
        <DialogTitle>Generate API Key?</DialogTitle>
        <DialogContent>
          <Typography>
            {config?.has_api_key
              ? 'Key lama akan diganti. Aplikasi CS AI yang masih memakai key lama akan berhenti bekerja.'
              : 'API key baru akan dibuat dan langsung aktif.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmGenerate(false)}>Batal</Button>
          <Button variant="contained" onClick={handleGenerate} disabled={generating}>
            {generating ? 'Membuat...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRevoke} onClose={() => setConfirmRevoke(false)}>
        <DialogTitle>Cabut API Key?</DialogTitle>
        <DialogContent>
          <Typography>
            Semua akses CS AI akan dinonaktifkan hingga Anda generate key baru.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRevoke(false)}>Batal</Button>
          <Button variant="contained" color="error" onClick={handleRevoke} disabled={revoking}>
            {revoking ? 'Mencabut...' : 'Cabut'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={newKeyDialog}
        onClose={() => setNewKeyDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>API Key Baru — Simpan Sekarang!</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Key ini hanya ditampilkan sekali. Salin dan simpan di environment aplikasi CS AI Anda.
          </Alert>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={generatedKey}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Salin API Key">
                    <IconButton onClick={() => copyText(generatedKey, 'API Key')}>
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setNewKeyDialog(false)}>
            Sudah Disalin
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CsApiSettings;
